import { SignupIntentType } from '../components/login-signup/SignupComponent';
import { TimezonePreferenceEnum } from '@/components/userSettings/EditTimezoneSettingsModal';
import { User } from '../types';
import { AddUserDeviceRequest, UpdateShowSimplifiedBdUISettingsRequest } from './appSettings';
import { UserUpdatable } from '@/actions/updatable';

export type CreateUserInput = {
    email: string;
    password: string;
    orgId: string;
};

export type SignUpRequest = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    hubSpotUserToken: string;
    intent: SignupIntentType;
    stapeId?: string;
    // Set when the signup page was reached via the MCP OAuth flow, which requires
    // an existing account (backend enforces via mcpFlowSignupGuard).
    mcpFlow?: boolean;
};

export interface NewPasswordRequest {
    userId: string;
    newPassword: string;
}

export interface VerifyCommsUserRequest {
    orgId: string;
    teamId: string;
    commsUserId: string;
    code: string;
    integrationType: string;
}

export type CreateUserResponse = {
    id: string;
    name: string;
};

export interface CreateUserAction {
    type: typeof UserUpdatable.CREATE_USER;
}

export interface TrySignUpAction {
    type: typeof UserUpdatable.TRY_SIGN_UP;
    payload: SignUpRequest;
}

export interface CreateUserSuccessAction {
    type: typeof UserUpdatable.CREATE_USER_SUCCESS;
    payload: CreateUserResponse;
}

export interface CreateUserFailureAction {
    type: typeof UserUpdatable.CREATE_USER_FAILURE;
    error: string;
}

export interface TryNewPasswordAction {
    type: typeof UserUpdatable.TRY_NEW_PASSWORD;
    payload: NewPasswordRequest;
}

export interface NewPasswordSuccessAction {
    type: typeof UserUpdatable.NEW_PASSWORD_SUCCESS;
    payload: User;
}

export interface UpdateTimezonePreferenceSuccessAction {
    type: typeof UserUpdatable.UPDATE_TIMEZONE_PREFERENCE_SUCCESS;
    payload: { timezonePreference: TimezonePreferenceEnum };
}

export interface UpdateShouldShowSimplifiedBdUISuccessAction {
    type: typeof UserUpdatable.UPDATE_SHOW_SIMPLIFIED_BD_UI_SUCCESS;
    payload: User;
}

export interface UpdateShouldShowSimplifiedBDUIAction {
    type: typeof UserUpdatable.UPDATE_SHOW_SIMPLIFIED_BD_UI;
    payload: UpdateShowSimplifiedBdUISettingsRequest;
}

export interface UpdateUserDevicesAction {
    type: typeof UserUpdatable.UPDATE_USER_DEVICES;
    payload: AddUserDeviceRequest;
}

export interface UpdateUserDevicesSuccessAction {
    type: typeof UserUpdatable.UPDATE_USER_DEVICES_SUCCESS;
    payload: User;
}

export interface VerifyCommsUserAction {
    type: typeof UserUpdatable.VERIFY_COMMS_USER;
    payload: VerifyCommsUserRequest;
}

export interface VerifyCommsUserSuccessAction {
    type: typeof UserUpdatable.VERIFY_COMMS_USER_SUCCESS;
}

// Actions
export const tryUpdateUserDevices: (payload: AddUserDeviceRequest) => UpdateUserDevicesAction = (payload) => {
    return {
        type: UserUpdatable.UPDATE_USER_DEVICES,
        payload,
    };
};

export const tryCreateUser = (input: CreateUserInput) => {
    return {
        type: UserUpdatable.CREATE_USER,
        payload: input,
    };
};

export const createUserSuccess = (response: CreateUserResponse) => {
    return {
        type: UserUpdatable.CREATE_USER_SUCCESS,
        payload: response,
    };
};

export const createUserFailure = (error: string) => {
    return {
        type: UserUpdatable.CREATE_USER_FAILURE,
        error: error,
    };
};

export const tryNewPassword: (payload: NewPasswordRequest) => TryNewPasswordAction = (payload) => {
    return {
        type: UserUpdatable.TRY_NEW_PASSWORD,
        payload,
    };
};

export const updateUserDevicesSuccess: (payload: User) => UpdateUserDevicesSuccessAction = (payload) => {
    return {
        type: UserUpdatable.UPDATE_USER_DEVICES_SUCCESS,
        payload,
    };
};

export const newPasswordSuccess: (payload: User) => NewPasswordSuccessAction = (payload) => {
    return {
        type: UserUpdatable.NEW_PASSWORD_SUCCESS,
        payload,
    };
};

export const tryUpdateShouldShowSimplifiedBdUI: (
    payload: UpdateShowSimplifiedBdUISettingsRequest,
) => UpdateShouldShowSimplifiedBDUIAction = (payload) => {
    return {
        type: UserUpdatable.UPDATE_SHOW_SIMPLIFIED_BD_UI,
        payload,
    };
};

export const updateShouldShowSimplifiedBdUISuccess: (payload: User) => UpdateShouldShowSimplifiedBdUISuccessAction = (
    payload,
) => {
    return {
        type: UserUpdatable.UPDATE_SHOW_SIMPLIFIED_BD_UI_SUCCESS,
        payload,
    };
};

export const updateTimezonePreferenceSuccess = (
    timezonePreference: TimezonePreferenceEnum,
): UpdateTimezonePreferenceSuccessAction => ({
    type: UserUpdatable.UPDATE_TIMEZONE_PREFERENCE_SUCCESS,
    payload: { timezonePreference },
});

export const trySignUp: (payload: SignUpRequest) => TrySignUpAction = (payload) => {
    return {
        type: UserUpdatable.TRY_SIGN_UP,
        payload: payload,
    };
};

export const tryVerifyCommsUser = (input: VerifyCommsUserRequest) => {
    return {
        type: UserUpdatable.VERIFY_COMMS_USER,
        payload: input,
    };
};

export const verifyCommsUserSuccess = () => {
    return {
        type: UserUpdatable.VERIFY_COMMS_USER_SUCCESS,
    };
};

export const verifyCommsUserFailure = (error: string) => {
    return {
        type: UserUpdatable.VERIFY_COMMS_USER_FAILURE,
        error: error,
    };
};

export type AdminActionTypes = CreateUserAction | CreateUserSuccessAction | CreateUserFailureAction;
